import { useCallback, useEffect, useRef, useState } from 'react'
import {
  getPurchaseScratchState,
  playPurchaseScratch,
  revealPurchaseScratch,
  scratchError,
  type PurchaseScratchAttempt,
  type PurchaseScratchState,
} from '@/lib/purchase-scratch'

const pendingStates = new Map<string, Promise<PurchaseScratchState>>()
function sharedState(campaignId: number, donorId: number) {
  const key = `${campaignId}:${donorId}`
  const existing = pendingStates.get(key)
  if (existing) return existing
  const request = getPurchaseScratchState(campaignId).finally(() => {
    pendingStates.delete(key)
  })
  pendingStates.set(key, request)
  return request
}

export function usePurchaseScratch(
  campaignId: number | undefined,
  donorId: number | undefined,
  refreshSignal: number
) {
  const [state, setState] = useState<PurchaseScratchState | null>(null)
  const [attempt, setAttempt] = useState<PurchaseScratchAttempt | null>(null)
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const lock = useRef<symbol | null>(null)
  const generation = useRef(0)
  const identity = `${campaignId}:${donorId}`
  const owner = useRef(identity)
  const requestKey = useRef<string | null>(null)
  useEffect(() => {
    owner.current = identity
    setState(null)
    setAttempt(null)
    setOpen(false)
    setError('')
    setBusy(false)
    lock.current = null
    generation.current++
    requestKey.current = null
    return () => {
      owner.current = ''
    }
  }, [identity])
  useEffect(() => {
    if (!campaignId || !donorId || lock.current) return
    let active = true
    const current = generation.current
    void sharedState(campaignId, donorId)
      .then((data) => {
        if (active && current === generation.current) {
          setState(data)
          setError('')
        }
      })
      .catch((err: unknown) => {
        if (active && current === generation.current) setError(scratchError(err))
      })
    return () => {
      active = false
    }
  }, [campaignId, donorId, refreshSignal])

  const start = useCallback(async () => {
    if (!campaignId || !donorId || lock.current) return
    generation.current++
    const operation = Symbol('start')
    lock.current = operation
    setOpen(true)
    setBusy(true)
    setError('')
    try {
      const fresh = await sharedState(campaignId, donorId)
      if (owner.current !== identity || lock.current !== operation) return
      setState(fresh)
      if (fresh.attempt) {
        setAttempt(fresh.attempt)
        return
      }
      if (!fresh.available) {
        setAttempt(null)
        return
      }
      const storageKey = `purchase-scratch:${identity}`
      // A lost response must be retried using the same key, including after a page reload.
      if (!requestKey.current) {
        try {
          requestKey.current = sessionStorage.getItem(storageKey)
        } catch {
          /* Memory still protects retries in this tab. */
        }
        requestKey.current ||= crypto.randomUUID()
        try {
          sessionStorage.setItem(storageKey, requestKey.current)
        } catch {
          /* Server also resumes unrevealed cards. */
        }
      }
      const card = await playPurchaseScratch(campaignId, requestKey.current)
      if (owner.current !== identity || lock.current !== operation) return
      if (card.revealed_at) {
        requestKey.current = null
        try {
          sessionStorage.removeItem(storageKey)
        } catch {
          /* A replay already completed in another tab can now advance. */
        }
      }
      setAttempt(card)
      setState((current) =>
        current
          ? {
              ...current,
              available: Math.max(
                0,
                current.available -
                  (current.history.some((item) => item.id === card.id) ? 0 : 1)
              ),
              pending: card.revealed_at ? 0 : 1,
              attempt: card.revealed_at ? null : card,
            }
          : current
      )
    } catch (err) {
      if (owner.current === identity && lock.current === operation)
        setError(scratchError(err))
    } finally {
      if (lock.current === operation) {
        lock.current = null
        if (owner.current === identity) setBusy(false)
      }
    }
  }, [campaignId, donorId, identity])

  const reveal = useCallback(async () => {
    if (!campaignId || !attempt || lock.current) return null
    generation.current++
    const operation = Symbol('reveal')
    lock.current = operation
    setBusy(true)
    setError('')
    try {
      const card = await revealPurchaseScratch(campaignId, attempt.id)
      if (owner.current !== identity || lock.current !== operation) return null
      setAttempt(card)
      setState((current) =>
        current
          ? {
              ...current,
              attempt: null,
              pending: 0,
              history: [
                card,
                ...current.history.filter((item) => item.id !== card.id),
              ].slice(0, 20),
            }
          : current
      )
      requestKey.current = null
      try {
        sessionStorage.removeItem(`purchase-scratch:${identity}`)
      } catch {
        /* Optional persistence. */
      }
      return card
    } catch (err) {
      if (owner.current === identity && lock.current === operation)
        setError(scratchError(err))
      return null
    } finally {
      if (lock.current === operation) {
        lock.current = null
        if (owner.current === identity) setBusy(false)
      }
    }
  }, [attempt, campaignId, identity])

  return {
    state,
    attempt,
    open,
    busy,
    error,
    start,
    reveal,
    close: () => setOpen(false),
    view: (card: PurchaseScratchAttempt) => {
      setAttempt(card)
      setError('')
    },
  }
}
